import { alpha } from '@mui/material/styles';

// Infographic palette (design convergence stage 1). Every colour in this file
// derives from these tokens; consumers read them through the MUI palette.
const getDesignTokens = (mode) => {
  const PALETTES = {
    light: {
      bg: '#f6f3ed', surface: '#ffffff', surface2: '#efeae0',
      ink: '#10292c', ink2: '#3d5557', muted: '#6f8385', line: '#dcd6ca',
      teal: '#255a5f', teal2: '#1d6f76', tealSoft: '#d5e7e7',
      accent: '#c9781d', accentSoft: '#f7e6cf', accentInk: '#8f5312',
      ok: '#1f7a4d', okSoft: '#dcefe4', no: '#b23b3b', noSoft: '#f5dede',
      onTeal: '#f2f8f7',
    },
    dark: {
      bg: '#0c1718', surface: '#132325', surface2: '#1a2e30',
      ink: '#e4eeed', ink2: '#b3c5c4', muted: '#7f9798', line: '#2a4245',
      teal: '#5fb3b3', teal2: '#7fc9c7', tealSoft: '#183a3c',
      accent: '#e9a24e', accentSoft: '#3a2a15', accentInk: '#e9a24e',
      ok: '#5cc28c', okSoft: '#163427', no: '#e77a7a', noSoft: '#3a1c1c',
      onTeal: '#0c1718',
    },
  };

  const c = mode === 'dark' ? PALETTES.dark : PALETTES.light;

  // Soft tones (stage 4): tinted background + strong same-hue text, shared by
  // Chip and Alert. `strong` is the text/icon/border colour, `soft` the fill.
  // accentInk is a darker amber so warning text stays readable on accentSoft.
  const TONES = {
    default: { strong: c.ink2, soft: c.surface2, line: c.line },
    primary: { strong: c.teal, soft: c.tealSoft },
    secondary: { strong: c.teal, soft: c.tealSoft },
    info: { strong: c.teal2, soft: c.tealSoft },
    success: { strong: c.ok, soft: c.okSoft },
    error: { strong: c.no, soft: c.noSoft },
    warning: { strong: c.accentInk, soft: c.accentSoft, line: c.accent },
  };
  const toneOf = (color) => TONES[color] || TONES.default;
  const lineOf = (t) => t.line || alpha(t.strong, 0.45);
  return ({
  palette: {
    mode,
    primary: { main: c.teal, light: c.teal2, contrastText: c.onTeal, soft: c.tealSoft },
    // secondary has always duplicated primary; it keeps doing so.
    secondary: { main: c.teal, light: c.teal2, contrastText: c.onTeal },
    background: {
      default: c.bg,
      paper: c.surface,
      neutral: c.tealSoft,
      table: c.surface2,
      chrome: c.surface2,
    },
    divider: c.line,
    border: { main: c.line },
    brand: {
      gradient: `linear-gradient(135deg, ${c.teal}, ${c.teal2})`,
    },
    text: {
      primary: c.ink,
      secondary: c.ink2,
      disabled: c.muted,
      default: c.ink,
      active: c.teal,   // selected/hovered nav text, sits on button.active
      ink: c.ink,       // emphasized value text
    },
    button: {
      active: c.tealSoft,  // hover / selected highlight behind text.active
      deactive: c.teal2,   // hover state of the mobile menu toggle (icon = background.default)
    },
    action: {
      active: c.ink2,
    },
    accent: { main: c.accent, soft: c.accentSoft },
    warning: {
      main: c.accent,
      light: c.accentSoft,
      dark: c.accent,
      contrastText: mode === 'dark' ? c.bg : c.ink,
      soft: c.accentSoft,
      ink: c.accentInk, // readable amber text on warning.soft / accent.soft
    },
    error: { main: c.no, soft: c.noSoft },
    info: { main: c.teal2, soft: c.tealSoft },
    success: { main: c.ok, soft: c.okSoft },
  },
  typography: {
    fontFamily: [
      'Vazirmatn',
      'Rubik',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    // Type scale (design convergence stage 2). Use variants for hierarchy,
    // never raw fontSize; Typography inherits colour and alignment.
    h1: { fontSize: 'clamp(1.625rem, 5vw, 2.5rem)', fontWeight: 900, lineHeight: 1.35 },
    h2: { fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.4 },
    h3: { fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.4 },
    h4: { fontSize: '1.125rem', fontWeight: 800, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.45 },
    h6: { fontSize: '0.90625rem', fontWeight: 700, lineHeight: 1.45 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.6 },
    subtitle2: { fontSize: '0.84375rem', fontWeight: 700, lineHeight: 1.6 },
    body1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.75 },
    body2: { fontSize: '0.84375rem', fontWeight: 400, lineHeight: 1.7 },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.6 },
    overline: { fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.6, letterSpacing: 0, textTransform: 'none' },
    button: { fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.75, textTransform: 'none' },
  },
  components: {
    MuiTablePagination: {
      styleOverrides: {
        root: {
          direction: 'ltr', // Force LTR for TablePagination component
          textAlign: 'left',
        },
        spacer: {
          flex: 'none', // Fix any layout issues related to spacing
        },
        actions: {
          direction: 'ltr', // Ensure the pagination buttons are in LTR mode
        },
        displayedRows: {
          direction: 'ltr', // Display pagination text (e.g., "1-10 of 100") in LTR
        },
      },
    },
    // No baked-in margins or fixed heights: spacing comes from the parent's
    // gap. minHeight (not height) so a wrapping label grows instead of clipping.
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem',
        },
        sizeMedium: {
          minHeight: '2.5rem', // 40px, matches the list toolbar and small inputs
        },
        sizeSmall: {
          minHeight: '2rem',
        },
      },
    },
    // Compact controls: small inputs are 40px high, level with medium buttons.
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: c.teal,
          "&.Mui-focused": {
            color: c.teal,
          },
          fontWeight: 'bold',
          fontSize: '0.8rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '.5rem',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '& .MuiInputBase-input': {
            backgroundColor: 'transparent',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: c.teal2,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: c.teal2,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: c.teal,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Use the table background color specified in your theme
          backgroundColor: c.surface,
          backgroundImage: 'none', // Remove any gradient backgrounds
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove any gradient backgrounds
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        variant: 'outlined', // all Papers will be outlined by default
        elevation: 0,        // remove shadows for a flat look
      },
      styleOverrides: {
        root: {
          backgroundColor: c.surface, // cards stand out from the background.default canvas
          backgroundImage: 'none',
          borderRadius: '0.5rem',
          // No default padding: Menus, Popovers, Dialogs and table containers are
          // Papers too. Section cards set it explicitly (p: 3, compact p: 2).
        },
      },
    },
    
    MuiTable: {
      styleOverrides: {
        root: {
          // For the table itself, you might only need to ensure it inherits the container's background
          // Text color will be handled at the TableCell level

        },
      },
    },
    // Tables (stage 4): tinted header row, hairline row separators, no zebra.
    MuiTableCell: {
      styleOverrides: {
        // Cells without an explicit `align` read from the start edge (right in
        // this RTL app) — logical, so the stylis RTL plugin leaves it alone.
        // Covers raw MUI tables (ledgers, assistant markdown tables).
        root: ({ ownerState }) => ({
          borderBottom: `1px solid ${c.line}`,
          ...(!ownerState.align || ownerState.align === 'inherit' ? { textAlign: 'start' } : {}),
        }),
        head: {
          backgroundColor: c.surface2,
          color: c.ink,
          fontWeight: 700,
          fontSize: '0.8125rem',
          lineHeight: 1.5,
        },
        // Opaque, so rows scrolling under a sticky header don't ghost through.
        stickyHeader: {
          backgroundColor: c.surface2,
        },
        footer: {
          color: c.ink,
          fontWeight: 700,
          fontSize: '0.84375rem',
        },
        // Tighter horizontal padding in dense tables so narrow columns
        // (dates, counts, statuses) don't widen the table.
        sizeSmall: {
          padding: '6px 8px',
        },
        sizeMedium: {
          padding: '12px 16px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: alpha(c.teal, 0.05),
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(c.teal, 0.1),
          },
        },
      },
    },
    // Chips (stage 4): soft tone per `color`. Plain chips are small badges
    // (6px radius); clickable/deletable chips are pills (filters, suggestions).
    // `variant="outlined"` keeps a transparent fill with a border in the tone.
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const t = toneOf(ownerState.color);
          const outlined = ownerState.variant === 'outlined';
          const interactive = ownerState.clickable || Boolean(ownerState.onDelete);
          return {
            height: 24,
            borderRadius: interactive ? 20 : 6,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: t.strong,
            backgroundColor: outlined ? 'transparent' : t.soft,
            border: `1px solid ${outlined ? lineOf(t) : 'transparent'}`,
            '& .MuiChip-icon': { color: t.strong },
            '& .MuiChip-deleteIcon': {
              color: alpha(t.strong, 0.7),
              '&:hover': { color: t.strong },
            },
            ...(ownerState.clickable && {
              '&:hover, &.Mui-focusVisible': {
                backgroundColor: outlined ? alpha(t.strong, 0.08) : alpha(t.strong, 0.2),
              },
            }),
          };
        },
        sizeSmall: {
          height: 22,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
    // Alerts (stage 4): the same soft tone per severity, hairline border.
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const t = toneOf(ownerState.severity || 'success');
          const variant = ownerState.variant || 'standard';
          if (variant === 'filled') {
            return ownerState.severity === 'warning'
              ? { backgroundColor: c.accent, color: mode === 'dark' ? c.bg : c.ink }
              : {};
          }
          return {
            borderRadius: '0.5rem',
            color: t.strong,
            backgroundColor: variant === 'outlined' ? 'transparent' : t.soft,
            border: `1px solid ${lineOf(t)}`,
            '& .MuiAlert-icon': { color: t.strong },
          };
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          // Define your default styles for MenuItem components here
          // Example styles:
          fontFamily: "Vazirmatn, Rubik, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
          fontSize: '.8rem', // Adjust the font size as needed
          color: c.ink,
          backgroundColor: 'transparent',

          '&:hover': {
            backgroundColor: 'transparent',
            color: c.teal, // Change text color on hover
          },
          '&.Mui-selected': {
            backgroundColor: c.tealSoft, // Background color for selected items
            color: c.teal, // Text color for selected items
            '&:hover': {
              backgroundColor: c.line, // Hover color for selected items
            },
          },
        },
      },
    },
  }
  });
};

export default getDesignTokens